// A task row's trailing controls in a list (WOS-BUNDLE): the timer's play / stop and a "…" menu
// with Open, Duplicate… and, on My Tasks in sections, "Move to…" (the keyboard twin of dragging a
// row to another section). Both are revealed on the row's hover or focus; the running timer stays.
// wp/ACTIONS: a hover Delete icon (and "Delete…" in the menu) asks first, then deletes the task.
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  type TaskSummary,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import {
  CopyIcon,
  EllipsisIcon,
  FolderInputIcon,
  PanelRightOpenIcon,
  Trash2Icon,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { DuplicateTaskDialog } from "./duplicate-dialogs";
import { DeleteTaskDialog, type DeletingTask } from "./task-delete";
import { RowTimerButton, useTimer } from "./task-time";

const ROW_REVEAL =
  "opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 data-popup-open:opacity-100 pointer-coarse:opacity-100";

export type MoveTarget = {
  sections: { id: string; name: string }[];
  /** The section the task is in now. */
  current: (taskId: string) => string;
  onMove: (taskId: string, sectionId: string) => void;
};

/** The `cells.actions` of a `ListView`, and the one Duplicate dialog the rows share. */
export function useTaskRowActions(
  api: Api,
  company: CompanyAccess,
  o: { onOpenTask: (id: string) => void; move?: MoveTarget | undefined },
): { actions: (task: TaskSummary) => ReactNode; dialog: ReactNode } {
  const timer = useTimer(api, company);
  const [copying, setCopying] = useState<{ id: string; title: string } | null>(null);
  const [deleting, setDeleting] = useState<DeletingTask | null>(null);
  const del = (task: TaskSummary) => setDeleting({ id: task.id, title: task.title });
  const actions = (task: TaskSummary) => (
    <>
      <RowTimerButton timer={timer} taskId={task.id} title={task.title} />
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Delete ${task.title}`}
              className={`${ROW_REVEAL} hover:text-destructive`}
              onClick={() => del(task)}
            />
          }
        >
          <Trash2Icon aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent>Delete</TooltipContent>
      </Tooltip>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Actions for ${task.title}`}
              className={ROW_REVEAL}
            />
          }
        >
          <EllipsisIcon aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-44">
          <DropdownMenuItem onClick={() => o.onOpenTask(task.id)}>
            <PanelRightOpenIcon aria-hidden="true" />
            Open
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setCopying({ id: task.id, title: task.title })}>
            <CopyIcon aria-hidden="true" />
            Duplicate…
          </DropdownMenuItem>
          {o.move ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <FolderInputIcon aria-hidden="true" />
                  Move to…
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="min-w-44">
                  <DropdownMenuRadioGroup
                    value={o.move.current(task.id)}
                    onValueChange={(v) => o.move?.onMove(task.id, String(v))}
                  >
                    {o.move.sections.map((s) => (
                      <DropdownMenuRadioItem key={s.id} value={s.id}>
                        {s.name}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={() => del(task)}>
            <Trash2Icon aria-hidden="true" />
            Delete…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
  const dialog = (
    <>
      {copying ? (
        <DuplicateTaskDialog
          api={api}
          company={company}
          task={copying}
          open
          onOpenChange={(v) => !v && setCopying(null)}
          onOpenTask={o.onOpenTask}
        />
      ) : null}
      <DeleteTaskDialog
        api={api}
        company={company}
        task={deleting}
        onClose={() => setDeleting(null)}
      />
    </>
  );
  return { actions, dialog };
}
